"use client";

import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, MessageCircle, Phone, X, ChevronDown, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader, SheetClose } from "@/components/ui/sheet";
import { CartIcon } from "@/components/shop/CartIcon";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  if (loading) {
    return <div className="flex gap-8">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-16 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <div ref={menuRef} className="relative">
      <ul className="flex items-center gap-6 xl:gap-8">
        {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        const isOpen = openDropdownId === item.id;
        return <li key={item.id} data-index={i} className="relative group py-2" onMouseEnter={() => hasChildren && setOpenDropdownId(item.id)} onMouseLeave={() => hasChildren && setOpenDropdownId(null)}>
              <Link to={item.href || "#"} aria-haspopup={hasChildren ? "true" : undefined} aria-expanded={hasChildren ? isOpen : undefined} onClick={e => {
            if (hasChildren) {
              e.preventDefault();
              setOpenDropdownId(isOpen ? null : item.id);
            }
          }} className="flex items-center gap-1.5 text-foreground text-[14px] xl:text-[15px] font-semibold uppercase tracking-[0.04em] transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]">
                <span>{item.label}</span>
                {hasChildren && <ChevronDown className={`size-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />}
              </Link>

              {hasChildren && <AnimatePresence>
                  {isOpen && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 8
            }} transition={{
              duration: 0.2,
              ease: "easeOut"
            }} className="absolute top-full left-0 pt-2 z-50 min-w-[220px]">
                      <div className="bg-popover text-popover-foreground border border-border rounded-xl shadow-lg p-2 flex flex-col gap-1 backdrop-blur-md">
                        {item.children?.map(child => <Link key={child.id} to={child.href || "#"} onClick={() => setOpenDropdownId(null)} className="px-3 py-2.5 rounded-lg text-sm font-medium tracking-wide transition-colors hover:bg-accent hover:text-accent-foreground text-foreground flex items-center justify-between">
                            <span>{child.label}</span>
                          </Link>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </li>;
      })}
      </ul>
    </div>;
}
function MobileMenu({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});
  const toggleSubMenu = (id: string) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col gap-6 px-6 pt-8">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-8 w-40 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <ul className="flex flex-col gap-2 px-6 pt-4">
      {menuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isSubOpen = openSubMenus[item.id] || false;
      return <motion.li key={item.id} data-index={i} initial={{
        opacity: 0,
        y: -12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.26,
        delay: i * 0.04,
        ease: "easeOut"
      }} className="border-b border-border/50 pb-2 last:border-0">
            <div className="flex items-center justify-between">
              <Link to={item.href || "#"} onClick={() => {
            if (!hasChildren) onNavigate();
          }} className="py-3 text-foreground text-2xl font-bold uppercase tracking-tight transition-colors hover:text-primary flex-1">
                {item.label}
              </Link>
              {hasChildren && <Button type="button" variant="ghost" size="icon" onClick={() => toggleSubMenu(item.id)} aria-haspopup="true" aria-expanded={isSubOpen} aria-label={`Toggle ${item.label} submenu`} className="text-foreground hover:bg-accent hover:text-accent-foreground">
                  <ChevronDown className={`size-5 transition-transform duration-200 ${isSubOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </Button>}
            </div>

            {hasChildren && <AnimatePresence>
                {isSubOpen && <motion.ul initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: "auto"
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.25,
            ease: "easeInOut"
          }} className="flex flex-col gap-2 pl-4 pb-3 overflow-hidden">
                    {item.children?.map(child => <li key={child.id}>
                        <Link to={child.href || "#"} onClick={onNavigate} className="block py-2 text-muted-foreground text-lg font-medium transition-colors hover:text-primary">
                          {child.label}
                        </Link>
                      </li>)}
                  </motion.ul>}
              </AnimatePresence>}
          </motion.li>;
    })}
    </ul>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const phone = "+34 657 068 444";
  const phoneHref = "tel:+34657068444";
  const whatsappHref = "https://wa.me/34657068444";
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="34750" id="cabecera-principal" data-nav="light" className="sticky top-0 z-50 w-full bg-background text-foreground">
      <motion.div animate={{
      boxShadow: scrolled ? "0px 1px 0px 0px hsl(34 15% 82%)" : "0px 0px 0px 0px hsl(34 15% 82% / 0)"
    }} transition={{
      duration: 0.18,
      ease: "easeOut"
    }} className="w-full border-b border-border">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-2.5 md:py-3.5">
          <div className="flex items-center justify-between gap-2.5 md:gap-8">
            <div className="flex items-center shrink-0">
              <Link to="/" aria-label="Servicios de reparaciones — Inicio" className="inline-flex items-center focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]">
                <WvcLogo className="h-11 md:h-14 w-auto max-w-[180px] sm:max-w-[220px] md:max-w-[260px] object-contain" />
              </Link>
            </div>

            <nav aria-label="Principal" className="hidden lg:flex flex-1 items-center justify-center">
              <MenuProvider menu_id="16">
                <DesktopMenu />
              </MenuProvider>
            </nav>

            <div className="flex items-center justify-end gap-1.5 sm:gap-2 md:gap-3 shrink min-w-0">
              <Link to="/MyAccountPage" aria-label="Mi Cuenta">
                <Button variant="ghost" size="icon" className="rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Mi Cuenta">
                  <User className="size-5" aria-hidden="true" />
                </Button>
              </Link>

              <CartIcon variant="ghost" size="icon" iconSize="size-5" className="rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground" />

              <Link to={whatsappHref} aria-label="WhatsApp Contacto" newTab>
                <Button className="rounded-lg bg-[#25D366] text-white hover:bg-[#20ba5a] h-10 md:h-11 px-3 md:px-4 text-[13px] md:text-[14px] font-semibold uppercase tracking-[0.04em] shadow-sm cursor-pointer">
                  <MessageCircle className="size-4" aria-hidden="true" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </Button>
              </Link>

              <Link to={phoneHref} aria-label={`Llamar ${phone}`}>
                <Button variant="outline" className="rounded-lg h-10 md:h-11 px-3 md:px-4 text-[13px] md:text-[14px] font-semibold uppercase tracking-[0.04em] shadow-sm">
                  <Phone className="size-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{phone}</span>
                </Button>
              </Link>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Abrir menú">
                    <Menu className="size-6" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="top" className="bg-background border-b border-border p-0 h-auto max-h-[90vh] overflow-y-auto">
                  <SheetHeader className="flex flex-row items-center justify-between px-6 py-5 border-b border-border">
                    <SheetTitle asChild>
                      <Link to="/" onClick={() => setMobileOpen(false)}>
                        <WvcLogo className="h-10 sm:h-12 w-auto max-w-full object-contain" />
                      </Link>
                    </SheetTitle>
                    <div className="flex items-center gap-2">
                      <SheetClose asChild>
                        <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Cerrar menú">
                          <X className="size-6" aria-hidden="true" />
                        </Button>
                      </SheetClose>
                    </div>
                  </SheetHeader>
                  <AnimatePresence>
                    <motion.div key="mobile-nav" initial={{
                    opacity: 0,
                    y: -12
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -12
                  }} transition={{
                    duration: 0.26,
                    ease: "easeOut"
                  }} className="pb-8">
                      <nav aria-label="Menú móvil">
                        <MenuProvider menu_id="16">
                          <MobileMenu onNavigate={() => setMobileOpen(false)} />
                        </MenuProvider>
                      </nav>
                      <div className="px-6 pt-6 flex flex-col gap-3">
                        <Link to="/MyAccountPage" onClick={() => setMobileOpen(false)} className="block">
                          <Button variant="outline" className="w-full rounded-lg h-12 px-6 text-[14px] font-semibold uppercase tracking-[0.06em] shadow-sm">
                            <User className="size-4" aria-hidden="true" />
                            <span>Mi Cuenta</span>
                          </Button>
                        </Link>
                        <Link to={whatsappHref} newTab onClick={() => setMobileOpen(false)} className="block">
                          <Button className="w-full rounded-lg bg-[#25D366] text-white hover:bg-[#20ba5a] h-12 px-6 text-[14px] font-semibold uppercase tracking-[0.06em] shadow-sm">
                            <MessageCircle className="size-4" aria-hidden="true" />
                            <span>WhatsApp</span>
                          </Button>
                        </Link>
                        <Link to={phoneHref} onClick={() => setMobileOpen(false)} className="block">
                          <Button className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 text-[14px] font-semibold uppercase tracking-[0.06em] shadow-sm">
                            <Phone className="size-4" aria-hidden="true" />
                            <span>{phone}</span>
                          </Button>
                        </Link>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}